<template>
  <b-form>
    <b-row>
      <b-col sm="6" class="mb-4">
        <b-form-group
          :label="formConfig.first_name.label"
          :label-for="formConfig.first_name.id"
          label-class="h6"
          :state="stateFirstName"
        >
          <b-form-input
            v-bind="formConfig.first_name"
            v-model.trim="formData.first_name"
            :state="stateFirstName"
            :disabled="!!isBooker"
          />
        </b-form-group>
      </b-col>
      <b-col sm="6" class="mb-4">
        <b-form-group
          :label="formConfig.last_name.label"
          :label-for="formConfig.first_name.id"
          label-class="h6"
          :state="stateLastName"
        >
          <b-form-input
            v-bind="formConfig.last_name"
            v-model="formData.last_name"
            :state="stateLastName"
            :disabled="!!isBooker"
          />
        </b-form-group>
      </b-col>
    </b-row>
    
    <b-row align-h="between">
      <b-col cols="auto" md="6" class="mb-4">
        <b-form-group 
          :label="formConfig.date_of_birth.label"
          :label-for="formConfig.date_of_birth.id"
          label-class="h6"
          :state="stateDob"
        >
          <DropdownDatepicker class="btb-datepicker"
            v-bind="formConfig.date_of_birth.properties"
            v-model="formData.date_of_birth"
            :defaultDate="formData.date_of_birth" 
          />
        </b-form-group>
      </b-col>
      <b-col cols="auto" md="6" class="mb-4">
        <b-form-group 
          :label="formConfig.postal_code.label"
          :label-for="formConfig.postal_code.id"
          label-class="h6"
          :state="statePostalCode"
        >
          <b-form-input 
            v-bind="formConfig.postal_code"
            v-model="formData.postal_code"
            :state="statePostalCode"
          />
        </b-form-group>
      </b-col>
      <!-- Phone number input, if state person phone number is null -->
      <b-col cols="auto" md="6" class="mb-4" v-if="isBooker && !bookerHasPhone">
        <b-form-group
          label="Country"
          label-for="countrySelect"
          :state="countryState"
          class="mb-4"
        >
          <b-form-select
            id="countrySelect"
            v-model="selectedCountryIso"
            :options="countryOptions"
            @change="onCountryChange"
            :state="countryState"
            required
          >
            <template #first>
              <option disabled value="">Select a country</option>
            </template>
          </b-form-select>
          <b-form-invalid-feedback>Please select a country.</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          :label="formConfig.phone.label"
          :label-for="formConfig.phone.id"
          :state="phoneState"
          class="mb-4"
        >
          <b-input-group
            :state="phoneState"
          >
            <b-input-group-prepend is-text>
              {{ selectedCountry.dialCode }}
            </b-input-group-prepend>
            <b-form-input
              :id="formConfig.phone.id"
              v-model="formData.phoneNumber"
              ref="phone"
              :state="phoneState"
              :type="formConfig.phone.type"
              @paste.prevent
              @copy.prevent
              @input="formatPhoneNumber"
              trim
            />
          </b-input-group>
          <b-form-invalid-feedback>Phone number is invalid.</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
    
    <b-row>
      <b-col cols="12" lg="6" class="mb-4">
        <!-- NOTE: conditional switch of these components depending on if they are online or on location -->
        <b-form-group 
          :label="formConfig[personTypeLocation].label"
          :label-for="formConfig[personTypeLocation].id"
          label-class="h6"
          :state="statePersonType"
          v-slot="{ ariaDescribedBy }"
        >
          <b-form-radio-group buttons button-variant="outline-dark" stacked
            class="d-flex flex-column align-items-start"
            :aria-describedby="ariaDescribedBy"
            :label-for="formConfig[personTypeLocation].id"
            :label="formConfig[personTypeLocation].label"
            :name="formConfig[personTypeLocation].name"
            :required="formConfig[personTypeLocation].required"
            :state="statePersonType"
            v-model="formData.personType"
            :validated="formSubmitted"
          >
            <b-form-radio class="btb-radio-button"
              v-for="{ text, value, description } in formConfig[personTypeLocation].options"
              :key="value"
              :value="value"
              :state="statePersonType"
            >
              <div>{{ text }}</div>
              <div class="font-small" v-if="description">{{ description }}</div>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>

      <b-col cols="12" lg="6" class="mb-4">
        <b-form-group 
          :label="formConfig.gender.label"
          :label-for="formConfig.gender.id"
          label-class="h6"
          :state="stateGender"
          v-slot="{ ariaDescribedBy }"
          :validated="formSubmitted"
        >
          <b-form-radio-group buttons button-variant="outline-dark" stacked
            class="d-flex flex-column align-items-start"
            :aria-describedby="ariaDescribedBy"
            :id="formConfig.gender.id"
            :label="formConfig.gender.label"
            :name="formConfig.gender.name"
            :required="formConfig.gender.required"
            :state="stateGender"
            v-model="formData.gender"
          >
            <b-form-radio class="btb-radio-button"
              v-for="{ text, value } in formConfig.gender.options"
              :key="value"
              :value="value"
            >
              {{ text }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
    </b-row>
    
    <b-row>
      <b-col>
        <b-input-group variant="outline-dark" :prepend="formConfig.social.prepend">
          <b-form-input
            v-bind="formConfig.social"
            v-model="formData.social"
          />
        </b-input-group>
        <label class="font-small text-muted">{{ formConfig.social.disclaimer }}</label>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import { BOOKER, PLAYER, SELECTED_RESERVATION } from '../../store/state';
import { IS_BOOKER } from '@/store/getters';
import DropdownDatepicker from 'vue-dropdown-datepicker';
import { UPDATE_PERSON, UPDATE_PLAYER } from '../../store/mutations';
import { getDisplayName, capitalizeFirstLetter } from '@/utils';
import { onlineOrOnLocation } from '@/utils/mixins/onlineOrOnLocation';
import { validateBeforeRouteLeave } from '@/utils/mixins/validateBeforeRouteLeave';
import { isOptionOther } from '../../utils';
import countriesData from '@/utils/countryCodes.json';
import { isPhoneNumberValid } from '@/utils/validationUtils'

export const PERSON_DETAILS_EVENTS = {
  COMPLETED: 'PersonDetailsStep:Completed',
}

export default {
  name: 'PersonDetails',
  mixins: [
    onlineOrOnLocation,
    // validateBeforeRouteLeave
  ],
  components: {
    DropdownDatepicker,
  },
  props: {
    person: {
      type: Object,
    },
    player: {
      type: Number,
    }
  },
  data () {
    const formConfig = {
      first_name: {
        id: 'person-first-name',
        label: 'First Name',
        placeholder: 'First Name',
        required: true,
        type: 'text',
      },
      last_name: {
        id: 'person-last-name',
        label: 'Last Name',
        placeholder: 'Last Name',
        required: true,
        type: 'text',
      },
      date_of_birth: {
        id: 'person-birthday',
        name: 'birthday',
        label: 'Your Birthday',
        properties: {
          daySuffixes: false,
          displayFormat: 'ymd',
          minAge: 18,
          minYear: 1940,
          required: true,
        },
        required: true
      },
      postal_code: {
        id: 'person-zipcode',
        name: 'zipcode',
        label: 'Your Zipcode',
        placeholder: 'Enter Zip Code',
        required: true,
        type: 'text',
      },
      personTypeOnline: {
        id: 'person-type-select',
        label: 'In what capacity are you filling out this waiver?',
        name: 'person-type',
        options: [
          {
            text: 'Player',
            description: '(Playing)',
            value: 1
          },
          {
            text: 'Spectator or Chaperone',
            description: '(Attending but not playing)',
            value: 2
          },
          {
            text: 'Parent or Guardian of a Minor',
            description: '(Not attending)',
            value: 3
          },
        ],
        required: true
      },
      personTypeOnLocation: {
        id: 'person-type-select',
        label: 'Are you playing today?',
        name: 'person-type',
        options: [
          {
            text: 'Yes',
            value: 1
          },
          {
            text: 'No',
            value: 2
          },
        ],
        required: true
      },
      gender: {
        id: 'person-gender-select',
        label: 'What is your gender?',
        name: 'person-gender',
        options: [
          {
            text: 'Female',
            value: 1
          },
          {
            text: 'Male',
            value: 2
          },
          {
            text: 'Non-binary',
            value: 3
          },
          {
            text: 'Prefer not to answer',
            value: 4
          },
        ],
        required: true
      },
      social: {
        disclaimer: 'Share your instagram handle to be featured on our social media channels.',
        id: 'person-social',
        name: 'social',
        label: 'Instagram Username',
        placeholder: 'Enter Instagram Handle',
        prepend: '@',
        type: 'text',
      },
      phone: {
        id: 'contact-phone',
        label: 'Phone Number',
        model: 'phoneNumber',
        placeholder: 'Enter Phone Number',
        type: 'tel'
      }
    }

    return {
      formConfig,
      formData: {
        first_name: this.person?.first_name ?? null,
        last_name: this.person?.last_name ?? null,
        date_of_birth: this.person?.date_of_birth ?? null,
        personType: this.player ?? formConfig[this.personTypeLocation]?.options.find(opt => isOptionOther(opt.text)), // TODO: set to the player option on either location
        gender: this.person?.gender_id ?? null,
        postal_code: this.person?.postal_code ?? null,
        social: this.person?.instagram ?? null,
        phoneNumber: this.person?.phone ?? null,
      },
      formSubmitted: false,
      selectedCountryIso: 'US',
      countries: countriesData
    }
  },
  computed: {
    ...mapState([
      BOOKER,
      PLAYER,
      SELECTED_RESERVATION,
    ]),
    ...mapGetters({
      isBooker: IS_BOOKER
    }),
    bookerHasPhone() {
      return this[BOOKER]?.has_phone_number === true;
    },
    canProceed () {
      const { first_name, last_name, date_of_birth, personType, gender, postal_code, phoneNumber } = this.formData;

      if (this.person?.phone || (this.isBooker && this.bookerHasPhone)) {
        console.log("Person phone is set, skipping phone validation");
        return !!(
          first_name
          && last_name
          && date_of_birth
          && personType
          && gender
          && postal_code
        )
      }

      return !!(
        first_name
        && last_name
        && date_of_birth
        && personType
        && gender
        && postal_code
        && phoneNumber
        && this.selectedCountryIso
      )
    },
    personTypeLocation () {
      return this.isOnline ? 'personTypeOnline' : 'personTypeOnLocation'
    },
    stateFirstName () {
      return this.formSubmitted
        ? !!this.formData?.first_name?.length
        : null
    },
    stateLastName () {
      return this.formSubmitted
        ? !!this.formData?.last_name?.length
        : null
    },
    stateDob () {
      return this.formSubmitted
        ? moment(this.formData?.date_of_birth).isValid()
        : null
    },
    statePersonType () {
      return this.formSubmitted
        ? !!this.formData.personType ?? false
        : null
    },
    stateGender () {
      return this.formSubmitted
        ? !!this.formData?.gender
        : null
    },
    statePostalCode () {
      return this.formSubmitted
        ? !!this.formData?.postal_code?.length
        : null
    },
    countryOptions () {
      return [
        { value: '', text: 'Select a country' },
        ...this.countries.map(c => ({
          value: c.isoCode,
          text: this.getFlagEmoji(c.isoCode) + ' ' + c.name
        }))
      ]
    },
    countryState() {
      return this.formSubmitted
        ? !!this.selectedCountryIso : null
    },
    phoneState() {
      var returnVal = this.formSubmitted ? !!isPhoneNumberValid(this.fullPhoneNumber) : null
      return returnVal;
    },
    selectedCountry(){
      const c = this.countries.find(x => x.isoCode === this.selectedCountryIso);
      return c || this.countries[0];
    },
    fullPhoneNumber() {
      if (this.formData.phoneNumber && isPhoneNumberValid(this.formData.phoneNumber)) {
        return this.formData.phoneNumber;
      } else {
        // console.log("Prepending country code to phone number from form");
        return this.selectedCountry.dialCode + this.formData.phoneNumber;
      }
    }
  },
  mounted () {
    if (this.canProceed) this.completeStep(this); 
  },
  watch: {
    canProceed (fresh) {
      if (fresh) this.completeStep(this);
    }
  },
  methods: {
    completeStep (isComplete) {
      this.$emit(PERSON_DETAILS_EVENTS.COMPLETED, isComplete ? this : null);
    },
    getDisplayName,
    capitalizeFirstLetter,
    updatePerson () {
      const {
        first_name, 
        last_name,
        personType,
        date_of_birth,
        gender,
        postal_code,
        social
      } = this.formData

      try {
        let fullPhone = this.fullPhoneNumber;

        this.$store.commit(UPDATE_PLAYER, personType)

        console.log("UpdatePlayer finished");

        let commit_object = {
          ...this.person,
          first_name,
          last_name,
          date_of_birth,
          gender_id: gender,
          postal_code,
          instagram: social,
          phone: fullPhone
        }

        if (this.isBooker && this.bookerHasPhone) {
          delete commit_object.phone;
        }

        this.$store.commit(UPDATE_PERSON, {
          ...commit_object
        })
      } catch (err) {
        console.error(`There was a problem updating the player information: ${err}`)
      }
    },
    validateForm () {
      console.log("Validating form");
      this.formSubmitted = true
      var actuallyCanProceed = this.canProceed;
      
      if (!this.isBooker || !this.bookerHasPhone){
        if (!this.person?.phone && !isPhoneNumberValid(this.fullPhoneNumber)) {
          console.log("Phone number is invalid, cannot proceed");
          actuallyCanProceed = false;
        }
      }

      if (actuallyCanProceed){
        this.updatePerson();
      }
      return actuallyCanProceed;
    },
    getFlagEmoji(countryCode) {
      return countryCode
        .toUpperCase()
        .replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)))
    },
    onCountryChange(_val) {
      this.formData.phoneNumber = ''
    },
    formatPhoneNumber(){
      this.formData.phoneNumber = this.formData.phoneNumber.replace(/\D/g, '')
    }
  },
  beforeRouteLeave (to, from, next) {
    console.log("Before route leave, updating person");
    if (!this.validateForm()){
      return next(false);
    }
    this.updatePerson()
    if (to.name === 'Promotion') {
      const isValid = this.validateForm()
      return next(isValid)
    }
    return next()
  }
}
</script>

<style scoped>

</style>